/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * Document Translator Pricing Module
 *
 * Handles pricing calculations for Document Translator product.
 * Pricing formula: a base price covers every document up to a word threshold,
 * then a per-word price applies to each word above it.
 *
 * THE RATE IS NOT KEPT HERE. The three numbers -- base price, per-word price
 * and threshold -- belong to the shop's back end, which charges the money from
 * them (backend/apps/checkout/document_translator_pricing.py) and publishes
 * them at GET /api/v1/prices/document-translator-rate/. The browser reads them
 * through redux/services/dtRateApi.ts and passes them in, so every price
 * computed here is computed from the numbers the shop will charge. A caller
 * that has no rate yet shows no price: see the "unknown" handling where the
 * cart reads the rate.
 *
 * @module document-translator-pricing
 */

// ============================================================================
// Constants
// ============================================================================

/**
 * Document Translator Stripe Product ID
 */
import { DOCUMENT_TRANSLATOR_PRODUCT_ID } from "@/config/product-ids";
import i18n from "@/i18n";
// N-10 (S629): formatNumber reads the shop's active language. A bare
// toLocaleString() follows the BROWSER's language instead, so one page
// could show translated words around numbers grouped for a different
// country than the one the customer chose.
import { formatNumber } from "@/lib/active-locale";
import { formatCurrencyCart } from "@/lib/money-format";
export { DOCUMENT_TRANSLATOR_PRODUCT_ID };

/**
 * Minimum allowed word count for purchase. Every document of one word or
 * more is priced; the base price covers the shortest documents.
 */
export const MINIMUM_WORD_COUNT = 1;

/**
 * Maximum allowed word count per order
 */
const MAXIMUM_WORD_COUNT = 1000000;

// ============================================================================
// Types
// ============================================================================

/**
 * The Document Translator rate, as the shop's back end publishes it.
 * All amounts are US cents.
 */
export interface DocumentTranslatorRate {
  /** What any document up to `baseWordThreshold` words costs. */
  basePriceCents: number;
  /** What each word above `baseWordThreshold` adds. */
  pricePerWordCents: number;
  /** The number of words the base price covers. */
  baseWordThreshold: number;
}

/**
 * Result of Document Translator price calculation
 */
export interface DocumentTranslatorPriceResult {
  /** Word count validated and used for calculation */
  wordCount: number;
  /** Base price component in cents */
  basePriceCents: number;
  /** Additional word charges in cents (for words above the threshold) */
  additionalWordsCents: number;
  /** Total price in cents */
  totalPriceCents: number;
  /** Total price in dollars (for display) */
  totalPriceDollars: number;
  /** Number of words above the base threshold */
  wordsAboveBase: number;
}

/**
 * Validation result for word count
 */
interface WordCountValidationResult {
  /** Whether the word count is valid */
  isValid: boolean;
  /** Error message if invalid */
  error: string | null;
  /** Suggested valid value if invalid */
  suggestedValue: number;
}

// ============================================================================
// Functions
// ============================================================================

/**
 * Check if a product ID is the Document Translator product
 *
 * @param productId - Stripe product ID to check
 * @returns True if this is Document Translator
 */
export function isDocumentTranslator(productId: string): boolean {
  return productId === DOCUMENT_TRANSLATOR_PRODUCT_ID;
}

/**
 * Validate word count for Document Translator purchase
 *
 * @param wordCount - Number of words to validate
 * @returns Validation result with error details
 */
export function validateWordCount(
  wordCount: number,
): WordCountValidationResult {
  // Handle invalid input types
  if (typeof wordCount !== "number" || isNaN(wordCount)) {
    return {
      isValid: false,
      error: i18n.t("cart.dt.wordCountInvalid"),
      suggestedValue: MINIMUM_WORD_COUNT,
    };
  }

  // Handle non-integer
  if (!Number.isInteger(wordCount)) {
    return {
      isValid: false,
      error: i18n.t("cart.dt.wordCountNotWhole"),
      suggestedValue: Math.max(MINIMUM_WORD_COUNT, Math.round(wordCount)),
    };
  }

  // Handle negative
  if (wordCount < 0) {
    return {
      isValid: false,
      error: i18n.t("cart.dt.wordCountNegative"),
      suggestedValue: MINIMUM_WORD_COUNT,
    };
  }

  // Handle zero
  if (wordCount === 0) {
    return {
      isValid: false,
      error: i18n.t("cart.dt.wordCountZero"),
      suggestedValue: MINIMUM_WORD_COUNT,
    };
  }

  // Handle below minimum
  if (wordCount < MINIMUM_WORD_COUNT) {
    return {
      isValid: false,
      error: i18n.t("cart.dt.wordCountBelowMinimum", {
        minimum: formatNumber(MINIMUM_WORD_COUNT),
        provided: formatNumber(wordCount),
      }),
      suggestedValue: MINIMUM_WORD_COUNT,
    };
  }

  // Handle above maximum
  if (wordCount > MAXIMUM_WORD_COUNT) {
    return {
      isValid: false,
      error: i18n.t("cart.dt.wordCountAboveMaximum", {
        maximum: formatNumber(MAXIMUM_WORD_COUNT),
      }),
      suggestedValue: MAXIMUM_WORD_COUNT,
    };
  }

  // Valid
  return {
    isValid: true,
    error: null,
    suggestedValue: wordCount,
  };
}

/**
 * A rate amount for display, by the cart's rule: the dollar sign first, a
 * space, and the customer's own digit grouping ("$ 3.00" in English, "$ 3,00"
 * in German) -- the same form as every other dollar line in the cart (VS-3,
 * S803 review). The amounts stay in dollars because that is the currency the
 * customer is charged in.
 */
export function formatRateAmount(cents: number): string {
  return formatCurrencyCart("usd", cents);
}

/**
 * The three figures of a rate, formatted, for sentences that name them
 * (`{{base}}`, `{{perWord}}`, `{{threshold}}` in the locale files).
 */
export function rateSentenceValues(rate: DocumentTranslatorRate): {
  base: string;
  perWord: string;
  threshold: string;
} {
  return {
    base: formatRateAmount(rate.basePriceCents),
    perWord: formatRateAmount(rate.pricePerWordCents),
    threshold: formatNumber(rate.baseWordThreshold),
  };
}

/**
 * Calculate Document Translator price based on word count, from the rate the
 * shop's back end published.
 *
 * @param wordCount - Number of words to translate
 * @param rate - The rate read from GET /api/v1/prices/document-translator-rate/
 * @returns Price calculation result
 *
 * @example
 * ```typescript
 * const result = calculateDocumentTranslatorPrice(150, rate);
 * // with the rate 300 / 3 / 100:
 * // result.totalPriceDollars = 4.50
 * ```
 */
export function calculateDocumentTranslatorPrice(
  wordCount: number,
  rate: DocumentTranslatorRate,
): DocumentTranslatorPriceResult {
  // Validate first
  const validation = validateWordCount(wordCount);
  const validWordCount = validation.isValid
    ? wordCount
    : validation.suggestedValue;

  // Calculate additional words (above base threshold)
  const wordsAboveBase = Math.max(0, validWordCount - rate.baseWordThreshold);

  // Calculate price components
  const basePriceCents = rate.basePriceCents;
  const additionalWordsCents = wordsAboveBase * rate.pricePerWordCents;
  const totalPriceCents = basePriceCents + additionalWordsCents;
  const totalPriceDollars = totalPriceCents / 100;

  return {
    wordCount: validWordCount,
    basePriceCents,
    additionalWordsCents,
    totalPriceCents,
    totalPriceDollars,
    wordsAboveBase,
  };
}

/**
 * Format word count for display with proper localization
 *
 * @param wordCount - Number of words
 * @returns Formatted string like "1,234 words"
 */
export function formatWordCount(wordCount: number): string {
  const count = Math.max(0, Math.floor(wordCount));
  // N-1 (S637): the sentence, not its fragments. English needs one plural rule
  // for "word"; the five other languages each need their own, and a caller
  // assembling "{number} {noun}" cannot express that.
  return count === 1
    ? i18n.t("common.wordCountOne", { count })
    : i18n.t("common.wordCount", { count });
}

/**
 * Get pricing formula description for UI display
 *
 * @param rate - The rate read from the shop's back end
 * @returns Human-readable pricing formula
 */
export function getPricingFormula(rate: DocumentTranslatorRate): string {
  // S657: this is the hover text on the word-count figure in the cart. The two
  // amounts stay in dollars because that is the currency the customer is
  // charged in -- only the words around them are translated.
  return i18n.t("cart.dt.pricingFormula", rateSentenceValues(rate));
}
